const BaseConfigService = require('./BaseConfigService');
const { globalLanguageCodes } = require('../../fields');

/**
 * Default Apple Pay version to use as fallback
 * @constant {number}
 */
const DEFAULT_APPLE_PAY_VERSION = 6;


/**
 * Apple Pay Configuration Service
 *
 * Manages Apple Pay specific configuration with validation and defaults
 */
class ApplePayConfigService extends BaseConfigService {
  /**
   * Get default Apple Pay configuration
   */
  _getDefaultConfig() {
    return {
      merchant_id: null,
      amount: null,
      gateway_code: null,
      country_code: null,
      currency_code: null,
      fee: null,
      fee_description: null,
      payment_url: null,
      shop_name: null,
      validation_url: null,
      merchantCapabilities: null,
      supportedNetworks: null,
      supportedCountries: null,
      version: null,
      lineItems: null,
      total: null,
      buttonLocale: null,
      theme: null,
      isApplePaySDKAvailable: false,
    };
  }

  /**
   * Apply configuration with mappings and validation
   */
  _applyConfig(config) {
    const mappings = {
      merchant_id: 'merchant_id',
      amount: 'amount',
      code: 'gateway_code',
      country_code: 'country_code',
      currency_code: 'currency_code',
      fee: 'fee',
      fee_description: 'fee_description',
      payment_url: 'payment_url',
      shop_name: 'shop_name',
      validation_url: 'validation_url',
      merchantCapabilities: 'merchantCapabilities',
      supportedNetworks: 'supportedNetworks',
      supportedCountries: 'supportedCountries',
      version: 'version',
      lineItems: 'lineItems',
      total: 'total',
      currencyCode: 'currency_code',
      countryCode: 'country_code',
      merchantIdentifier: 'merchant_id',
      buttonLocale: 'buttonLocale',
      theme: 'theme',
      isApplePaySDKAvailable: 'isApplePaySDKAvailable',
    };

    Object.entries(mappings).forEach(([sourceKey, targetKey]) => {
      if (typeof config[sourceKey] !== 'undefined') {
        // Special validation for buttonLocale
        if (sourceKey === 'buttonLocale') {
          if (this._validateApplePayButtonLocale(config[sourceKey])) {
            this._config[targetKey] = config[sourceKey];
          }
        } else {
          this._config[targetKey] = config[sourceKey];
        }
      }
    });
  }

  /**
   * Validate Apple Pay button locale
   */
  _validateApplePayButtonLocale(value) {
    return globalLanguageCodes.includes(value);
  }

  /**
   * Check if Apple Pay is configured
   */
  isConfigured() {
    return !!(this._config.merchant_id || this._config.validation_url);
  }

  /**
   * Set SDK Studio Apple Pay configurations
   */
  setSDKStudioConfigs(applePaymentService = {}, studioConfigs = {}) {
    const { code } = applePaymentService;
    const applePayConfigs = studioConfigs.applePay;

    if (!code || !Array.isArray(applePayConfigs) || !applePayConfigs.length) {
      return;
    }

    const matchingConfig = applePayConfigs.find(
      (config) => config.code === code
    );
    if (!matchingConfig) {
      return;
    }

    const {
      version,
      buttonLocale,
      supportedNetworks,
      merchantCapabilities,
      supportedCountries,
    } = matchingConfig;

    this.updateConfig({
      version,
      buttonLocale,
      supportedNetworks,
      merchantCapabilities,
      supportedCountries,
    });
  }

  /**
   * Resolves the Apple Pay version to use based on device support.
   * Attempts to use the configured version from SDK Studio, then falls back
   * to lower versions if not supported.
   *
   * @returns {number} The highest supported Apple Pay version
   */
  resolveVersion() {
    const configuredVersion = this._config.version;

    if (
      typeof window !== 'undefined' &&
      window.ApplePaySession &&
      typeof window.ApplePaySession.supportsVersion === 'function'
    ) {
      // If version is configured from SDK Studio, loop down from it to find highest supported
      if (typeof configuredVersion === 'number' && configuredVersion > 0) {
        for (let version = configuredVersion; version >= 1; version--) {
          try {
            if (window.ApplePaySession.supportsVersion(version)) {
              return version;
            }
          } catch (error) {
            continue;
          }
        }

        return configuredVersion;
      }

      for (let version = DEFAULT_APPLE_PAY_VERSION; version >= 1; version--) {
        try {
          if (window.ApplePaySession.supportsVersion(version)) {
            return version;
          }
        } catch (error) {
          continue;
        }
      }
    }

    return configuredVersion || DEFAULT_APPLE_PAY_VERSION;
  }
}

// Export singleton instance
module.exports = new ApplePayConfigService();
